import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [needsRegistration, setNeedsRegistration] = useState(false);

  useEffect(() => {
    const restoreUser = async () => {
      // ローカルストレージからトークンとユーザー情報を復元
      const savedToken = localStorage.getItem('auth_token');
      const savedUser = localStorage.getItem('user_info');

      if (savedToken && savedUser) {
        try {
          const userInfo = JSON.parse(savedUser);
          // まずユーザーを設定
          setToken(savedToken);
          setUser(userInfo);
          setIsAuthenticated(true);

          // db_user_idがない場合、APIから取得
          if (!userInfo.db_user_id && userInfo.id) {
            const dbUserId = await fetchUserDbId(userInfo);
            // 再取得できなければローカルストレージをクリアし、ログアウト
            if (!dbUserId) {
              console.warn('Failed to fetch db_user_id, logging out');
              localStorage.removeItem('auth_token');
              localStorage.removeItem('user_info');
              setUser(null);
              setToken(null);
              setIsAuthenticated(false);
            }
          }
        } catch (error) {
          console.error('Failed to restore user session:', error);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_info');
        }
      }

      setIsLoading(false);
    };

    restoreUser();
  }, []);

  const fetchUserDbId = async (userInfo, retryCount = 0) => {
    try {
      const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '/api';
      const userResponse = await fetch(`${API_BASE}/users/${userInfo.id}`);
      const userData = await userResponse.json();

      if (userData.exists && userData.user.id) {
        userInfo.db_user_id = userData.user.id;
        setUser({...userInfo});
        localStorage.setItem('user_info', JSON.stringify(userInfo));
        return userData.user.id; // 成功時はdb_user_idを返す
      }
      return null; // ユーザーが存在しない場合
    } catch (error) {
      console.error(`Failed to fetch user db_user_id (attempt ${retryCount + 1}/2):`, error);
      // リトライは1回まで
      if (retryCount < 1) {
        console.log('Retrying fetchUserDbId...');
        return await fetchUserDbId(userInfo, retryCount + 1);
      }
      return null; // 2回目も失敗した場合はnullを返す
    }
  };

  const login = async (userInfo, authToken) => {
    try {
      // Test loginの場合（開発環境のみ許可）
      if (userInfo.id === 'test-user-123') {
        // 本番環境ではテストログインを許可しない
        if (process.env.NODE_ENV !== 'development') {
          console.error('Test login is not allowed in production');
          return false;
        }

        // テストユーザーにはdb_user_idを設定しない（音声ログはスキップされる）
        setUser(userInfo);
        setToken(authToken);
        setIsAuthenticated(true);
        setNeedsRegistration(false);

        localStorage.setItem('auth_token', authToken);
        localStorage.setItem('user_info', JSON.stringify(userInfo));

        console.log('Test user logged in:', userInfo);
        return true;
      }

      const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '/api';

      // ユーザーが既に存在するかチェック
      const userResponse = await fetch(`${API_BASE}/users/${userInfo.id}`);
      const userData = await userResponse.json();

      if (!userData.exists) {
        // 新規ユーザーの場合、データベースに作成
        const createResponse = await fetch(`${API_BASE}/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            google_id: userInfo.id,
            email: userInfo.email,
            name: userInfo.name,
            given_name: userInfo.given_name,
            family_name: userInfo.family_name,
            picture: userInfo.picture
          })
        });

        if (!createResponse.ok) {
          const errorData = await createResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to create user');
        }

        const newUserData = await createResponse.json();
        userInfo.db_user_id = newUserData.user_id;
        setNeedsRegistration(true);
      } else {
        // 既存ユーザーの場合、プロフィール完了状況をチェック
        setNeedsRegistration(!userData.user.is_profile_complete);
        // データベースのuser_idを追加
        userInfo.db_user_id = userData.user.id;
      }

      setUser(userInfo);
      setToken(authToken);
      setIsAuthenticated(true);

      // ローカルストレージに保存
      localStorage.setItem('auth_token', authToken);
      localStorage.setItem('user_info', JSON.stringify(userInfo));

      console.log('User logged in:', userInfo);
      return true;
    } catch (error) {
      console.error('Login error:', error);
      // エラー時はログイン失敗としてエラーメッセージを返す
      // ローカルストレージには保存しない
      return { success: false, message: error.message };
    }
  };

  const loginAsGuest = () => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(true);
    setIsGuest(true);
    setNeedsRegistration(false);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    setIsGuest(false);
    setNeedsRegistration(false);
    
    // ローカルストレージから削除
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_info');
    
    console.log('User logged out');
  };

  const completeRegistration = () => {
    setNeedsRegistration(false);
  };

  const value = {
    user,
    isAuthenticated,
    isGuest,
    isLoading,
    token,
    needsRegistration,
    login,
    loginAsGuest,
    logout,
    completeRegistration
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};